<template>
  <!-- Detail panel for a node, at two densities:
         density="full"    — the List detail: roomy, editable (status select, due date, notes, accept
                            LLM status), with metrics, the story timeline, and rich mentions.
         density="compact" — the Map detail: tight, read-only, with a close button; relationships +
                            lean mentions only. -->
  <div :class="full ? 'p-5 space-y-5' : 'p-3 space-y-2'">
    <!-- Header -->
    <div class="flex items-start justify-between" :class="full ? 'gap-3' : 'gap-2'">
      <div class="min-w-0">
        <!-- Type eyebrow: neutral glyph + singular label (type carries no color, it's read by icon) -->
        <div class="flex items-center gap-1.5 uppercase tracking-wide font-semibold text-gray-400" :class="full ? 'text-[11px]' : 'text-[10px]'">
          <font-awesome-icon :icon="['fas', typeIcon(node.type)]" class="text-gray-400" />{{ typeLabel(node.type) }}
        </div>
        <component :is="full ? 'h2' : 'h3'" class="font-semibold text-gray-900 leading-snug mt-1" :class="full ? 'text-lg' : 'text-sm text-gray-800'">{{ node.title }}</component>
        <ET_MetaRow
          v-if="full"
          class="mt-1.5"
          :node="node"
          :show-owner="!nextStepText"
          :link-count="relationships.length"
          last-label="last discussed"
          size="md"
        />
      </div>
      <div v-if="full" class="flex items-center gap-2 shrink-0">
        <span v-if="isSaving" class="text-[11px] text-gray-400">Saving…</span>
        <span v-if="confidencePct(node.confidence)" class="text-[11px] text-gray-400">{{ confidencePct(node.confidence) }} confidence</span>
      </div>
      <button v-else class="text-gray-400 hover:text-gray-600 text-sm leading-none shrink-0" @click="$emit('select', null)">✕</button>
    </div>

    <!-- Compact: read-only badge cluster + tight meta -->
    <template v-if="!full">
      <div class="flex items-center gap-1.5 flex-wrap">
        <span
          v-if="node.type !== 'initiative'"
          class="inline-flex items-center gap-1 uppercase tracking-wide font-semibold rounded-full text-[10px] px-1.5 py-0.5"
          :class="statusClass(node.status)"
        ><span aria-hidden="true" class="leading-none">{{ statusGlyph(node.status) }}</span>{{ statusLabel(node.status) }}</span>
        <span
          v-if="node.severity"
          class="inline-flex items-center uppercase tracking-wide font-semibold rounded-full text-[10px] px-1.5 py-0.5"
          :style="severityStyle(node.severity)"
        >{{ node.severity }}</span>
        <span
          v-if="node.prioritized_at"
          class="inline-flex items-center gap-1 uppercase tracking-wide font-semibold rounded-full text-[10px] px-1.5 py-0.5 bg-amber-50 text-amber-700"
          :title="node.prioritized_by_name ? 'Prioritized by ' + node.prioritized_by_name : 'Prioritized for next meeting'"
        ><font-awesome-icon :icon="['fas', 'star']" class="text-[9px]" />Priority</span>
        <span
          v-if="stanceLabel"
          class="inline-flex items-center uppercase tracking-wide font-semibold rounded-full text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-600"
        >{{ stanceLabel }}</span>
        <ET_TrajectoryBadge v-if="node.type === 'initiative'" :trajectory="node.trajectory" />
      </div>
      <ET_MetaRow :node="node" :show-owner="!nextStepText" :show-mentions="false" :show-due="false" />
    </template>

    <!-- Full: editable controls row -->
    <div v-if="full" class="flex items-center gap-2 flex-wrap">
      <select
        v-if="node.type !== 'initiative'"
        :value="node.status"
        :disabled="isSaving"
        class="text-[10px] uppercase tracking-wide font-semibold pl-2 pr-5 py-1 rounded border-0 cursor-pointer focus:ring-1 focus:ring-accent"
        :class="statusClass(node.status)"
        @change="$emit('change-status', node, $event.target.value)"
      >
        <option v-for="s in STATUS_OPTIONS" :key="s" :value="s">{{ statusGlyph(s) }} {{ statusLabel(s) }}</option>
      </select>
      <span
        v-if="node.severity"
        class="inline-flex items-center uppercase tracking-wide font-semibold rounded-full text-[10px] px-1.5 py-0.5"
        :style="severityStyle(node.severity)"
      >{{ node.severity }} severity</span>
      <button
        type="button"
        class="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wide font-semibold transition-colors"
        :class="node.prioritized_at ? 'border-amber-200 bg-amber-100 text-amber-800' : 'border-gray-300 bg-white text-gray-500 hover:border-amber-300 hover:text-amber-700'"
        :disabled="isSaving"
        :title="node.prioritized_by_name ? 'Prioritized by ' + node.prioritized_by_name : 'Prioritize for next meeting'"
        @click="$emit('toggle-priority', node)"
      ><font-awesome-icon :icon="['fas', 'star']" class="text-[9px]" />{{ node.prioritized_at ? 'Priority' : 'Prioritize' }}</button>
      <span
        v-if="stanceLabel"
        class="inline-flex items-center uppercase tracking-wide font-semibold rounded-full text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-600"
      >{{ stanceLabel }}</span>
      <ET_TrajectoryBadge v-if="node.type === 'initiative'" :trajectory="node.trajectory" />
      <!-- Sentiment: the practice's attitude across this initiative's beats — neutral word + face glyph
           (a separate axis from trajectory/pace). Color stays reserved for health. -->
      <span
        v-if="nodeSentiment"
        class="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-semibold text-gray-400"
        :title="`Attitude across the last ${nodeSentiment.n} beat(s): ${nodeSentiment.pos} positive, ${nodeSentiment.neg} negative`"
      ><font-awesome-icon :icon="['fas', nodeSentiment.icon]" />{{ nodeSentiment.label }}</span>
    </div>

    <!-- Full: who set the status override + when (only when a human has overridden the LLM's read). -->
    <p v-if="full && node.user_status" class="text-[11px] text-gray-400 -mt-2">
      Status set by {{ node.user_status_by_name || "a teammate" }}<template v-if="node.user_status_at"> · {{ formatDate(node.user_status_at) }}</template>
    </p>

    <p v-if="full && node.prioritized_at" class="text-[11px] text-amber-700 -mt-2">
      <font-awesome-icon :icon="['fas', 'star']" class="mr-1 text-[9px]" />Prioritized<template v-if="node.prioritized_by_name"> by {{ node.prioritized_by_name }}</template> · {{ formatDate(node.prioritized_at) }}
    </p>

    <!-- Full: needs-review banner -->
    <div v-if="full && node.needs_status_review" class="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 flex items-center justify-between gap-3">
      <span class="text-xs text-amber-800">
        <font-awesome-icon :icon="['fas', 'triangle-exclamation']" class="mr-1" />
        Latest reading suggests this is <strong>{{ statusLabel(node.llm_status) }}</strong>.
      </span>
      <button class="px-2 py-1 rounded bg-amber-100 text-amber-800 text-xs font-medium hover:bg-amber-200 whitespace-nowrap" @click="$emit('accept-llm', node)">Accept</button>
    </div>

    <!-- Description -->
    <p v-if="node.description" class="text-gray-600 leading-relaxed" :class="full ? 'text-sm' : 'text-xs line-clamp-5'">{{ node.description }}</p>

    <!-- Next step -->
    <ET_NextStep :text="nextStepText" :owner="ownerLabel(node)" :size="full ? 'md' : 'sm'" />

    <!-- (initiative metrics live on the Story tab's live chart, not duplicated here) -->

    <!-- Full: operational (due date + notes) -->
    <div v-if="full" class="space-y-3 pt-1">
      <div class="flex items-center gap-2 text-xs">
        <label class="text-gray-400 w-16">Due date</label>
        <input
          type="date"
          :value="node.due_date || ''"
          class="border border-gray-200 rounded px-2 py-1 text-xs text-gray-600"
          @change="$emit('patch-field', node, 'due_date', $event.target.value || null)"
        />
      </div>
      <div>
        <label class="text-xs text-gray-400">Notes</label>
        <textarea
          :value="node.notes"
          rows="2"
          placeholder="Add a note…"
          class="w-full mt-1 text-xs border border-gray-200 rounded px-2 py-1.5 text-gray-600"
          @blur="$emit('patch-notes', node, $event.target.value)"
        ></textarea>
      </div>
    </div>

    <!-- Relationships (click to jump to the linked item) — count only on the roomy full detail. -->
    <div v-if="relationships.length" class="space-y-1.5">
      <p class="uppercase tracking-wide font-semibold text-gray-400" :class="full ? 'text-[11px]' : 'text-[10px]'">
        Relationships<template v-if="full"> · {{ relationships.length }}</template>
      </p>
      <div v-for="(rel, i) in relationships" :key="i" class="py-0.5">
        <div class="flex items-center gap-1.5">
          <button
            type="button"
            class="min-w-0 flex-1 text-left text-xs text-gray-600 flex items-center gap-1.5 hover:text-gray-900"
            @click="$emit('select', rel.otherId)"
          >
            <span class="text-[10px] font-semibold px-1.5 py-0.5 rounded-full shrink-0" :style="edgeStyle(rel.edge_type)">{{ rel.label }}</span>
            <span class="truncate">{{ rel.otherTitle }}</span>
          </button>
          <!-- Edge confidence — a whisper, full detail only (the compact Map pane stays lean). -->
          <span v-if="full && rel.confidence != null" class="text-[10px] text-gray-400 tabular-nums shrink-0">{{ confidencePct(rel.confidence) }}</span>
          <!-- Evidence-quote affordance: unwinds the verbatim quote backing this link (causal edges). -->
          <button
            v-if="rel.evidence_quote"
            type="button"
            class="text-gray-300 hover:text-accent text-[11px] leading-none shrink-0"
            :title="relOpen[i] ? 'Hide quote' : 'Show evidence quote'"
            @click="toggleRel(i)"
          >
            <font-awesome-icon :icon="['fas', 'quote-right']" />
          </button>
        </div>
        <!-- Quote box — same idiom as a Story beat's quote; click opens the transcript at the quote. -->
        <button
          v-if="relOpen[i] && rel.evidence_quote"
          type="button"
          class="mt-1 w-full text-left rounded-md bg-gray-50 border border-gray-100 px-2.5 py-1.5 hover:border-accent group"
          @click="rel.evidence_engagement && $emit('open-transcript', rel.evidence_engagement, rel.evidence_quote)"
        >
          <p class="text-xs italic text-gray-600 group-hover:text-gray-800 leading-relaxed">"{{ rel.evidence_quote }}"</p>
        </button>
      </div>
    </div>

    <!-- Full: the shared story timeline (same beats as Story-all / Story-focused) -->
    <div v-if="full && beats.length" class="space-y-1.5">
      <p class="text-[11px] uppercase tracking-wide font-semibold text-gray-400">Story · {{ beats.length }}</p>
      <ET_StoryTimeline
        :beats="beats"
        @open-transcript="(e, q) => $emit('open-transcript', e, q)"
        @open-node="$emit('open-node', $event)"
        @show-metric="showMetric"
      />
    </div>

    <!-- Mentions (transcript quotes; click to open the transcript at the quote). Full = rich (engagement
         label + hover magnifier); compact = lean (date + quote). -->
    <div v-if="quotes.length" class="space-y-1.5">
      <p class="uppercase tracking-wide font-semibold text-gray-400" :class="full ? 'text-[11px]' : 'text-[10px]'">Mentions · {{ quotes.length }}</p>
      <button
        v-for="(ev, i) in quotes"
        :key="ev.id || i"
        type="button"
        class="w-full text-left border-l-2 border-gray-200 hover:border-accent group"
        :class="full ? 'pl-2.5 py-1' : 'pl-2 py-0.5'"
        @click="$emit('open-transcript', ev.engagement, ev.quote)"
      >
        <div v-if="full" class="flex items-center gap-1.5 text-[11px] text-gray-400 mb-0.5">
          <span class="font-medium text-gray-500">{{ formatDate(ev.engagement_date) || "Engagement" }}</span>
          <span v-if="evidenceLabel(ev)" class="truncate">· {{ evidenceLabel(ev) }}</span>
          <font-awesome-icon :icon="['fas', 'magnifying-glass']" class="ml-auto text-gray-300 opacity-0 group-hover:opacity-100 text-[10px]" />
        </div>
        <span v-else class="text-[10px] text-gray-400">{{ formatDate(ev.engagement_date) }}</span>
        <p class="text-xs italic text-gray-600 group-hover:text-gray-800">"{{ ev.quote }}"</p>
      </button>
    </div>

    <ET_MetricBeatPopup :popup="metricPopup" @close="metricPopup = null" />
  </div>
</template>

<script>
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { library } from "@fortawesome/fontawesome-svg-core";
import {
  faTriangleExclamation, faChartLine, faMagnifyingGlass, faQuoteRight,
  faBullseye, faBan, faSquareCheck, faComment, faCircleInfo,
  faFaceSmile, faFaceMeh, faFaceFrown, faStar,
} from "@fortawesome/free-solid-svg-icons";
import ET_MetaRow from "@/components/EngagementTracker/parts/ET_MetaRow.vue";
import ET_TrajectoryBadge from "@/components/EngagementTracker/parts/ET_TrajectoryBadge.vue";
import ET_NextStep from "@/components/EngagementTracker/parts/ET_NextStep.vue";
import ET_StoryTimeline from "@/components/EngagementTracker/parts/ET_StoryTimeline.vue";
import ET_MetricBeatPopup from "@/components/EngagementTracker/parts/ET_MetricBeatPopup.vue";
import {
  fmt, edgesFor as fmtEdgesFor, sentimentOf, STATUS_OPTIONS,
  typeIcon, typeLabel, edgeStyle, severityStyle, beatsFor, buildMetricPopup,
} from "@/components/EngagementTracker/engagementTrackerHelpers.js";

library.add(faTriangleExclamation, faChartLine, faMagnifyingGlass, faQuoteRight, faBullseye, faBan, faSquareCheck, faComment, faCircleInfo, faFaceSmile, faFaceMeh, faFaceFrown, faStar);

export default {
  name: "ET_NodeDetail",
  components: {
    FontAwesomeIcon, ET_MetaRow, ET_TrajectoryBadge,
    ET_NextStep, ET_StoryTimeline, ET_MetricBeatPopup,
  },
  props: {
    node: { type: Object, required: true },
    density: { type: String, default: "full" },
    edges: { type: Array, default: () => [] },
    titleById: { type: Object, default: () => ({}) },
    // Full node set, so the story timeline resolves node_ref chips (full density only).
    allNodes: { type: Array, default: () => [] },
    metricNames: { type: Object, default: () => ({}) },
    saving: { type: Set, default: () => new Set() },
  },
  emits: ["select", "change-status", "accept-llm", "patch-field", "patch-notes", "open-transcript", "open-node", "toggle-priority"],
  data() {
    // relOpen: which relationship rows have their evidence quote unwound (keyed by row index).
    return { STATUS_OPTIONS, metricPopup: null, relOpen: {} };
  },
  computed: {
    full() {
      return this.density === "full";
    },
    isSaving() {
      return this.saving.has(this.node.id);
    },
    nextStepText() {
      return (this.node.metadata && this.node.metadata.next_step) || "";
    },
    stanceLabel() {
      return fmt.stanceLabel(this.node);
    },
    relationships() {
      return fmtEdgesFor(this.edges, this.titleById, this.node.id);
    },
    quotes() {
      return fmt.quotesFor(this.node);
    },
    beats() {
      return this.full ? beatsFor(this.node, this.allNodes) : [];
    },
    // Per-initiative sentiment (full detail only) — net attitude across this initiative's beats.
    nodeSentiment() {
      return this.full && this.node.type === "initiative" ? sentimentOf(this.beats) : null;
    },
  },
  watch: {
    // Reset transient per-node UI state when the selection changes.
    "node.id"() {
      this.relOpen = {};
      this.metricPopup = null;
    },
  },
  methods: {
    typeIcon,
    typeLabel,
    edgeStyle,
    severityStyle,
    confidencePct: fmt.confidencePct,
    statusClass: fmt.statusClass,
    statusLabel: fmt.statusLabel,
    statusGlyph: fmt.statusGlyph,
    ownerLabel: fmt.ownerLabel,
    fmtRate: fmt.fmtRate,
    formatDate: fmt.formatDate,
    evidenceLabel: fmt.evidenceLabel,
    toggleRel(i) {
      this.relOpen = { ...this.relOpen, [i]: !this.relOpen[i] };
    },
    // Build the metric-reading popover for a story beat's "Metric" chip — values as-of the meeting.
    showMetric({ node, engagement, event }) {
      this.metricPopup = buildMetricPopup(node, engagement, event, this.metricNames);
    },
  },
};
</script>
